/** A picked suggestion as the address forms take it; a part Google did not give is ''. */
export interface SuggestedAddress {
  line1: string;
  city: string;
  state: string;
  postalCode: string;
  countryAlpha2: string;
}

/** One part of a place's address, as Google's Place gives it. */
export interface PlaceAddressComponent {
  longText: string | null;
  shortText: string | null;
  types: string[];
}

/**
 * Countries that write the house number before the street ("1600 Amphitheatre
 * Pkwy"). Everywhere else it follows the street ("Herzl St 10").
 */
const NUMBER_FIRST = new Set([
  'US', 'CA', 'GB', 'IE', 'AU', 'NZ', 'FR', 'LU', 'MC', 'IN', 'PH', 'SG', 'MY', 'HK', 'ZA', 'SA', 'AE',
  'PK', 'LK', 'NG', 'KE', 'GH', 'JM', 'TT', 'BS', 'BB', 'BM', 'PR', 'GU', 'VI', 'AS', 'MP',
]);

/**
 * The address of a picked place, from its address components. The street is the
 * short form ("Pkwy", not "Parkway") so it fits the 35 characters couriers take;
 * a part the place does not have is left empty for the user to fill in.
 */
export function addressFromPlace(components: readonly PlaceAddressComponent[] | null | undefined): SuggestedAddress {
  const part = (type: string, form: 'short' | 'long' = 'long'): string => {
    const component = (components ?? []).find((each) => each.types?.includes(type));
    const text = form === 'short'
      ? component?.shortText ?? component?.longText
      : component?.longText ?? component?.shortText;
    return (text ?? '').trim();
  };

  const countryAlpha2 = part('country', 'short').toUpperCase();
  const number = part('street_number');
  const street = part('route', 'short');
  let line1 = street;
  if (street && number) {
    line1 = NUMBER_FIRST.has(countryAlpha2) ? `${number} ${street}` : `${street} ${number}`;
  }

  return {
    line1,
    city:
      part('locality') ||
      part('postal_town') ||
      part('sublocality_level_1') ||
      part('administrative_area_level_3') ||
      part('administrative_area_level_2'),
    state: part('administrative_area_level_1', 'short'),
    postalCode: part('postal_code'),
    countryAlpha2,
  };
}
